
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useState } from 'react';

export default function Home() {
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);

  const stats = [
    { label: 'Investment Pool', value: '€1M+' },
    { label: 'Key LATAM Markets', value: '5+' },
    { label: 'Banking Experience', value: '40+ Years' },
    { label: 'Pilot Kickoff', value: 'Q4 2026' }
  ];

  const features = [
    {
      icon: 'ri-global-line',
      title: 'Cross-Border Expertise',
      description: 'Deep understanding of European and Latin American markets with proven track record in international expansion.'
    },
    {
      icon: 'ri-bank-line',
      title: 'Financial Discipline',
      description: 'Senior banking executives providing financial restructuring, capital optimization, and strategic guidance.'
    },
    {
      icon: 'ri-team-line',
      title: 'Founder Partnership',
      description: 'Selective partnerships with exceptional founders, providing hands‑on operational guidance and mentorship.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Market Entry Strategy',
      description: 'Navigate LATAM regulatory environment and accelerate market penetration with our proven methodology.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.7), rgba(15, 23, 42, 0.7)), url('https://public.readdy.ai/ai/img_res/6e67fde23202811e29b5670e6c5921ae.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-4 leading-tight" style={{ fontFamily: '"Playfair Display", serif' }}>
              We fund Europe's<br />boldest founders
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-200 mb-3 md:mb-4">
              — and help them conquer LATAM.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-slate-300 mb-6 md:mb-8 max-w-2xl">
              Luxembourg-based venture capital firm bridging Europe and Latin America through strategic seed investments. 
              Committed to deploy over €1M capital with senior banking expertise to accelerate your LATAM expansion.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Link to="/pitch#pitch-form">
                <Button variant="primary" size="md" className="w-full sm:w-auto">
                  Apply for Investment
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" size="md" className="border-white text-white hover:bg-white hover:text-slate-900 w-full sm:w-auto">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Bridging Europe and Latin America
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                We partner with exceptional European founders who are ready to expand into Latin America's dynamic markets. 
                Our unique combination of venture capital and senior banking expertise provides the strategic advantage needed 
                to navigate complex cross‑border expansion.
              </p>
              <p className="text-lg text-slate-600 mb-8">
                With former LATAM executives from Santander and Bank of America, we offer more than capital — we provide 
                the operational expertise, financial discipline, and market insights that turn ambitious visions into 
                successful realities.
              </p>
              <Link to="/investment-thesis">
                <Button variant="primary">
                  Our Investment Approach
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20business%20meeting%20between%20European%20and%20Latin%20American%20executives%20in%20modern%20conference%20room%2C%20diverse%20international%20team%20discussing%20investment%20strategy%2C%20financial%20documents%20on%20table%2C%20global%20business%20partnership%2C%20corporate%20handshake%2C%20cross-cultural%20collaboration%2C%20premium%20office%20environment&width=600&height=400&seq=mission-img&orientation=landscape"
                alt="International business collaboration"
                className="rounded-lg shadow-lg object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Why Choose EULA VC
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We combine venture capital innovation with financial acumen, providing founders with the strategic 
              advantage needed for successful LATAM expansion.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Expand into Latin America?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            Join our selective portfolio of European startups successfully scaling across LATAM markets. 
            Apply for our $1M seed investment and strategic partnership program.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pitch#pitch-form">
              <Button variant="primary" size="lg">
                Apply for Investment
              </Button>
            </Link>
            <button
              onClick={() => setShowSchedulePopup(true)}
              className="px-8 py-3 border border-white text-white hover:bg-white hover:text-slate-900 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule a Call
            </button>
          </div>
        </div>
      </section>

      {/* Schedule Meeting Popup */}
      {showSchedulePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-slate-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Schedule a Meeting
              </h3>
              <p className="text-slate-600 mb-6">
                To schedule a call with our team, please first submit your pitch materials through our "Pitch Us" page. 
                This helps us prepare for a more productive conversation about your LATAM expansion plans.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/pitch" className="flex-1">
                  <Button variant="primary" className="w-full">
                    Submit Pitch Materials
                  </Button>
                </Link>
                <button
                  onClick={() => setShowSchedulePopup(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
